import type { WorkOrderResponse } from '@palaccount/contracts';
import * as React from 'react';

import type { TypeaheadOption } from '@/components/ui/typeahead';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

import { useWorkOrderSearch, useWorkOrdersHeld } from './api';

/**
 * THE WORK-ORDER PICKER (GEN-50) — MFG-04's ledger filter and its voucher
 * form, the last two `<select>`s in PART 9.
 *
 * PAGINATION.md §5a carried this route through four waves as "left, and now a
 * straight conversion": nothing conceptual blocked it once the typeahead
 * existed, and it asked for `MAX_PAGE_SIZE` in the meantime — bounded, but a
 * silent cut at 500 jobs all the same.
 *
 * `heldId` is the job the form is holding (a voucher links to exactly one, and
 * the ledger filter selects one). It is fetched separately from the search for
 * the reason the component spells out, and here that reason is sharper than
 * usual: a correction voucher names the job it corrects, and by the time
 * anybody writes a correction that job is very often CLOSED.
 */
export function useWorkOrderPicker(heldId: string | null | undefined): PickerState<WorkOrderResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);

  const matches = useWorkOrderSearch(search);
  const held = useWorkOrdersHeld(ids);

  return usePickerState<WorkOrderResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: workOrderOption,
    loading: matches.isFetching,
  });
}

/**
 * MFG-04 §5.2 and §5.5 both print the job by its NUMBER, which is what a
 * storekeeper is given; the finished item rides along as the hint because two
 * numbers a week apart are otherwise indistinguishable on a panel. The search
 * behind this matches both, so a row can never appear for a reason its label
 * does not show.
 */
export function workOrderOption(job: WorkOrderResponse): TypeaheadOption {
  return { id: job.id, label: job.code, hint: job.finishedItemName };
}
